import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Award,
  ArrowDown,
  CircleHelp,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Filter,
  Gamepad2,
  Globe,
  HardDriveDownload,
  Heart,
  Maximize2,
  Monitor,
  Package,
  Play,
  Puzzle,
  RotateCcw,
  ShieldCheck,
  Tag,
  Trophy,
} from "lucide-react";
import { api, type Game, type Offer, type Shop } from "@/lib/api";
import { listingIdentity } from "@/lib/offer-identity";
import { comparableEur } from "@/lib/fx";
import { shopCategoryLabelKey } from "@/lib/shop-categories";
import {
  normalizeOfferDrm as normalizeDrm,
  normalizeOfferPlatform as normalizePlatform,
  normalizeOfferRegion as normalizeRegion,
  type DrmInfo,
  type PlatformKind,
  type RegionInfo,
} from "@/lib/offer-visuals";

import { EmptyState, ErrorState } from "@/components/empty-state";
import { useWishlist } from "@/lib/wishlist";
import { useSettings } from "@/lib/settings";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  SteamFeaturesCard,
  SupportedLanguagesCard,
  SystemRequirementsCard,
} from "@/components/game-info-cards";
import { SimilarGames } from "@/components/similar-games";
import { GameDetailBackdrop } from "@/components/game-detail-backdrop";
import { ShopLogo } from "@/components/shop-logo";
import { PriceAlertButton } from "@/components/price-alert-button";
import { PriceHistorySection } from "@/components/price-history-chart";


export const Route = createFileRoute("/_site/game/$slug")({
  loader: async ({ params }) => params,
  head: ({ params }) => ({
    meta: [
      { title: `${humanize(params.slug)} – Compare prices & buy the cheapest key | NexKey.gg` },
      { name: "description", content: `Compare prices for ${humanize(params.slug)} across trusted stores. Steam price, discounts, regions and DRM at a glance.` },
      { property: "og:title", content: `${humanize(params.slug)} — NexKey.gg` },
      { property: "og:description", content: `Compare prices for ${humanize(params.slug)} across every shop.` },
      { property: "og:type", content: "product" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `/game/${params.slug}` }],
  }),
  component: GameDetail,
});

function humanize(slug: string) {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

import { createContext, useContext } from "react";
import { trackAffiliateClick } from "@/lib/analytics";

interface GameCtx { id: string; name: string }
const GameContext = createContext<GameCtx | null>(null);
function useGameCtx() { return useContext(GameContext); }

function fireAffiliate(row: StoreRow, ctx: GameCtx | null) {
  trackAffiliateClick({
    gameId: ctx?.id,
    gameName: ctx?.name,
    shopName: row.shopName,
    platform: row.platform,
    price: row.effectivePrice ?? row.price ?? undefined,
    currency: row.currency,
    url: row.url,
  });
}

/* ---------------- Unified row model ---------------- */

interface StoreRow {
  key: string;
  shopName: string;
  shopSlug: string;
  shopLogo: string | null;
  price: number | null;
  originalPrice: number | null;
  currency: string;
  discountPercent: number | null;
  platform: string | null;
  drm: string | null;
  region: string | null;
  edition: string | null;
  stockStatus: string;
  url: string | null;
  isSteam: boolean;
  isBest?: boolean;
  /** Coupon-adjusted price (equals price when no coupon). */
  effectivePrice: number | null;
  /** Coupon code the user must enter to unlock the effectivePrice. */
  couponCode: string | null;
  couponPercent: number | null;
}




function offerRow(o: Offer): StoreRow {

  const isSteam = o.shop.slug === "steam";
  const effectivePrice = o.effectivePrice ?? o.price;
  return {
    key: o.id,
    shopName: o.shop.name,
    shopSlug: o.shop.slug,
    shopLogo: o.shop.logoUrl ?? null,
    price: o.price,
    originalPrice: o.originalPrice ?? null,
    currency: o.currency,
    discountPercent:
      o.originalPrice && o.originalPrice > o.price
        ? Math.round(((o.originalPrice - o.price) / o.originalPrice) * 100)
        : null,
    platform: o.platform ?? null,
    drm: o.drmType ?? null,
    region: o.region ?? null,
    edition: o.edition ?? null,
    stockStatus: o.stockStatus || "in_stock",
    url: o.affiliateUrl || o.directUrl || null,
    isSteam,
    effectivePrice,
    couponCode: o.couponCode ?? null,
    couponPercent: o.couponPercent ?? null,
  };
}


/* ---------------- Component ---------------- */

// Preferred display order for known editions.
const EDITION_ORDER = [
  "Standard", "Deluxe", "Super Deluxe", "Premium", "Gold",
  "Ultimate", "Complete", "Definitive", "Legendary",
  "GOTY", "Collector's", "Season Pass", "Upgrade", "DLC",
];

function GameDetail() {
  const { slug } = Route.useParams();
  const { t, formatPrice, currency: siteCurrency, fxRates } = useSettings();
  const { has, toggle: toggleWishlist } = useWishlist();
  const game = useQuery({ queryKey: ["game", slug], queryFn: () => api.game(slug) });
  const offers = useQuery({ queryKey: ["game-offers", slug], queryFn: () => api.gameOffers(slug) });

  // IMPORTANT: every hook in this component must run before the early
  // returns below. Declaring state/effects after a conditional return
  // changes the hook count between renders (loading -> loaded) and makes
  // React throw, which bubbles up to the global route error boundary.

  // Collapse ONLY true currency duplicates of the exact same external listing
  // (e.g. the same GameBillet product imported from the EUR/GBP/USD feeds).
  // Different listings from the same shop — other regions, DRM, editions or
  // external ids — stay visible as separate rows, because region/platform/DRM
  // are meaningful buying dimensions.
  const rawOffers = offers.data?.data ?? [];
  const byListing = new Map<string, typeof rawOffers[number]>();
  for (const o of rawOffers) {
    const key = listingIdentity(o);
    const existing = byListing.get(key);
    if (!existing) { byListing.set(key, o); continue; }
    const existingMatches = existing.currency === siteCurrency;
    const candidateMatches = o.currency === siteCurrency;
    if (candidateMatches && !existingMatches) { byListing.set(key, o); continue; }
    if (candidateMatches === existingMatches) {
      const ep = comparableEur(o.effectivePrice ?? o.price, o.currency, fxRates);
      const xp = comparableEur(existing.effectivePrice ?? existing.price, existing.currency, fxRates);
      if (ep < xp) byListing.set(key, o);
    }
  }
  const usedOffers = Array.from(byListing.values());
  const allRows: StoreRow[] = usedOffers.map(offerRow);

  // Group by edition (normalised to "Standard" when missing).
  const editions = Array.from(
    new Set(allRows.map((r) => (r.edition && r.edition.trim()) || "Standard")),
  );
  editions.sort((a, b) => {
    const ai = EDITION_ORDER.indexOf(a);
    const bi = EDITION_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const [activeEdition, setActiveEdition] = useState<string>(editions[0] ?? "Standard");
  // If the current selection disappeared (e.g. offers refetched), reset.
  useEffect(() => {
    if (editions.length && !editions.includes(activeEdition)) {
      setActiveEdition(editions[0]);
    }
  }, [editions.join("|")]);
  // "DLCs" is a separate product-family view, not an edition.
  const [productView, setProductView] = useState<"offers" | "dlcs">("offers");
  useEffect(() => { setProductView("offers"); }, [slug]);

  if (game.isLoading) {
    return (
      <div className="container mx-auto px-4 py-24 text-center text-muted-foreground">
        {t("loading")}
      </div>
    );
  }
  if (game.isError) {
    return (
      <div className="container mx-auto px-4 py-12">
        <ErrorState message={(game.error as Error).message} />
      </div>
    );
  }

  const g = game.data!.data;
  const dlcList = g.type === "dlc" ? [] : (g.dlcs ?? []);

  const rows: StoreRow[] = allRows.filter(
    (r) => ((r.edition && r.edition.trim()) || "Standard") === activeEdition,
  );

  // Compare in a common currency (EUR) — never raw numbers across currencies.
  rows.sort((a, b) =>
    comparableEur(a.effectivePrice ?? a.price, a.currency, fxRates) -
    comparableEur(b.effectivePrice ?? b.price, b.currency, fxRates));
  if (rows.length > 0 && (rows[0].effectivePrice ?? rows[0].price) != null) rows[0].isBest = true;

  const storeCount = new Set(rows.map((r) => r.shopName)).size;
  const purchasableRows = rows.filter(
    (r) =>
      !!r.url &&
      (r.effectivePrice ?? r.price ?? 0) > 0 &&
      (r.stockStatus === "in_stock" || r.stockStatus === "preorder"),
  );
  const topOffers = purchasableRows.slice(0, 3).map((row, index) => ({
    ...row,
    isBest: index === 0,
  }));



  const platformList = [
    g.platforms?.windows && "Windows",
    g.platforms?.mac && "macOS",
    g.platforms?.linux && "Linux",
  ].filter(Boolean).join(" · ") || "PC (Windows)";

  return (
    <div className="relative">
      {/* Cinematic game-artwork atmosphere behind the whole hero area. */}
      <GameDetailBackdrop game={g} />
      {/* ============ 1. HERO (title + meta above gallery) ============ */}
      <section className="relative isolate overflow-hidden border-b border-border/60">
        <div className="container relative mx-auto px-4 pt-6 pb-8 md:pt-8 md:pb-10">
          <nav className="mb-4 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">{t("game.breadcrumb.home")}</Link>
            <span>/</span>
            <Link to="/games" className="hover:text-foreground">{t("game.breadcrumb.games")}</Link>
            <span>/</span>
            {g.parentGame && (
              <>
                <span>/</span>
                <Link to="/game/$slug" params={{ slug: g.parentGame.slug }} className="truncate hover:text-foreground">
                  {g.parentGame.title}
                </Link>
              </>
            )}
            <span>/</span>
            <span className="truncate text-foreground">{g.title}</span>
          </nav>

          {g.type === "dlc" && (
            <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-primary">
                <Puzzle className="h-3.5 w-3.5" />
                {t("game.dlc.badge")}
              </span>
              {g.parentGame && (
                <span className="text-muted-foreground">
                  {t("game.dlc.requires")}{" "}
                  <Link
                    to="/game/$slug"
                    params={{ slug: g.parentGame.slug }}
                    className="font-semibold text-foreground underline-offset-2 hover:text-primary hover:underline"
                  >
                    {g.parentGame.title}
                  </Link>
                </span>
              )}
            </div>
          )}

          <div className="mb-4 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
            <h1 className="min-w-0 font-display text-3xl font-black leading-tight md:text-5xl">{g.title}</h1>
            <div className="hidden shrink-0 items-center gap-2 sm:flex">
              <Button
                type="button"
                variant="outline"
                onClick={() => toggleWishlist(g.id)}
                aria-pressed={has(g.id)}
                aria-label={t("game.action.wishlist")}
                title={t("game.action.wishlist")}
                className={has(g.id) ? "border-primary/60 bg-primary/10 text-primary" : ""}
              >
                <Heart className={has(g.id) ? "fill-current" : ""} />
                <span className="hidden xl:inline">{t("game.action.wishlist")}</span>
              </Button>
              <PriceAlertButton
                className="w-auto rounded-md py-2"
                gameId={g.id}
                gameTitle={g.title}
                currentPrice={rows[0]?.effectivePrice ?? rows[0]?.price ?? null}
                currentCurrency={rows[0]?.currency ?? g.steamCurrency ?? "EUR"}
              />
            </div>
          </div>

          <GameContext.Provider value={{ id: g.id, name: g.title }}>
            <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-[minmax(0,8fr)_minmax(20rem,3fr)] lg:gap-6">
              {/* LEFT: gallery */}
              <div className="min-w-0">
              <HeroGallery
                title={g.title}
                movies={g.movies ?? []}
                screenshots={g.screenshots ?? []}
                fallback={g.headerImage ?? g.capsuleImage ?? g.backgroundImage ?? null}
              />
              </div>

              {/* RIGHT: active-edition top offers */}
              <aside className="min-w-0 lg:h-full">
                <TopOffersPanel
                  game={g}
                  rows={topOffers}
                  offerCount={purchasableRows.length}
                  formatPrice={formatPrice}
                />
              </aside>
            </div>
          </GameContext.Provider>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:hidden">
            <Button
              type="button"
              variant="outline"
              onClick={() => toggleWishlist(g.id)}
              aria-pressed={has(g.id)}
              className={has(g.id) ? "border-primary/60 bg-primary/10 text-primary" : ""}
            >
              <Heart className={has(g.id) ? "fill-current" : ""} />
              {t("game.action.wishlist")}
            </Button>
            <PriceAlertButton
              className="rounded-md py-2"
              gameId={g.id}
              gameTitle={g.title}
              currentPrice={rows[0]?.effectivePrice ?? rows[0]?.price ?? null}
              currentCurrency={rows[0]?.currency ?? g.steamCurrency ?? "EUR"}
            />
          </div>
        </div>
      </section>

      {/* ============ 2. PRICE COMPARISON ============ */}
      <section id="price-comparison" className="container mx-auto scroll-mt-20 px-4 pt-10 pb-12">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold md:text-3xl">
              {t("game.section.priceComparison")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {rows.length} {rows.length === 1 ? "offer" : "offers"} across {storeCount} {storeCount === 1 ? "store" : "stores"}
              {editions.length > 1 && ` · ${activeEdition} edition`}
            </p>
          </div>
          <div className="hidden items-center gap-1.5 rounded-full border border-border/60 bg-surface/60 px-3 py-1.5 text-xs text-muted-foreground sm:inline-flex">
            <ShieldCheck className="h-3.5 w-3.5 text-success" />
            {t("game.verified")}
          </div>
        </div>

        {(editions.length > 1 || dlcList.length > 0) && (
          <div className="mb-5 flex flex-wrap items-center gap-2 [&>div]:mb-0">
          {editions.length > 1 && (
          <EditionTabs
            editions={editions}
            active={productView === "dlcs" ? "" : activeEdition}
            onChange={(e) => { setActiveEdition(e); setProductView("offers"); }}
            counts={Object.fromEntries(
              editions.map((e) => [
                e,
                allRows.filter((r) => ((r.edition && r.edition.trim()) || "Standard") === e).length,
              ]),
            )}
          />
          )}
          {dlcList.length > 0 && (
            <button
              type="button"
              onClick={() => setProductView(productView === "dlcs" ? "offers" : "dlcs")}
              aria-pressed={productView === "dlcs"}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${
                productView === "dlcs"
                  ? "border-primary bg-primary text-primary-foreground shadow-lg"
                  : "border-border/60 bg-card/60 text-muted-foreground hover:bg-surface hover:text-foreground"
              }`}
            >
              <Puzzle className="h-4 w-4" />
              {t("game.dlcs")} ({dlcList.length})
            </button>
          )}
          </div>
        )}

        {productView === "dlcs" && dlcList.length > 0 ? (
          <DlcGrid dlcs={dlcList} formatPrice={formatPrice} />
        ) : <>

        {offers.isLoading ? (
          <div className="rounded-2xl border border-border/60 bg-card p-8 text-center text-sm text-muted-foreground">
            {t("loading")}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState title={t("empty.offers")} />
        ) : (
          <GameContext.Provider value={{ id: g.id, name: g.title }}>
            <PriceTable rows={rows} formatPrice={formatPrice} />
          </GameContext.Provider>
        )}
        </>}

        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="h-3 w-3" />
          {t("game.priceNote")}
          <Link to="/legal/affiliate" className="ml-1 underline hover:text-foreground">
            {t("game.affiliate")}
          </Link>
        </p>
      </section>


      {/* ============ 2b. PRICE HISTORY ============ */}
      <PriceHistorySection slug={g.slug} />

      {/* ============ 3. TABS SECTION ============ */}
      <section className="container mx-auto px-4 pb-12">
        <GameTabs game={g} platformList={platformList} />
      </section>

      {/* ============ 4. YOU MAY ALSO LIKE ============ */}
      <SimilarGames game={g} />







      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: g.title,
            image: g.headerImage,
            description: g.shortDescription,
            offers: rows
              .filter((r) => r.price != null)
              .map((r) => ({
                "@type": "Offer",
                price: (r.price! / 100).toFixed(2),
                priceCurrency: r.currency,
                availability:
                  r.stockStatus === "in_stock"
                    ? "https://schema.org/InStock"
                    : "https://schema.org/OutOfStock",
                url: r.url ?? undefined,
                seller: { "@type": "Organization", name: r.shopName },
              })),
          }),
        }}
      />
    </div>
  );
}

/* ---------------- Sub components ---------------- */

function DlcGrid({
  dlcs,
  formatPrice,
}: {
  dlcs: NonNullable<Game["dlcs"]>;
  formatPrice: (cents: number | null | undefined, currency?: string) => string;
}) {
  const { t } = useSettings();
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {dlcs.map((d) => (
        <Link
          key={d.id}
          to="/game/$slug"
          params={{ slug: d.slug }}
          className="group flex items-center gap-3 overflow-hidden rounded-xl border border-border/60 bg-card p-2.5 transition hover:border-primary/60 hover:bg-surface"
        >
          <div className="aspect-[460/215] w-32 shrink-0 overflow-hidden rounded-lg bg-surface">
            {(d.headerImage || d.capsuleImage) && (
              <img
                src={d.headerImage ?? d.capsuleImage ?? ""}
                alt={d.title}
                loading="lazy"
                className="h-full w-full object-cover transition group-hover:scale-105"
              />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="line-clamp-2 text-sm font-semibold leading-snug group-hover:text-primary">{d.title}</div>
            <div className="mt-1 flex items-baseline gap-2 text-xs text-muted-foreground">
              {d.bestOffer != null && (
                <span>
                  {t("game.dlc.from")}{" "}
                  <span className="text-base font-bold text-foreground">{formatPrice(d.bestOffer, d.steamCurrency ?? "EUR")}</span>
                </span>
              )}
              {!!d.storeCount && <span>· {d.storeCount} {t("game.dlc.offers")}</span>}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function EditionTabs({
  editions,
  active,
  onChange,
  counts,
}: {
  editions: string[];
  active: string;
  onChange: (e: string) => void;
  counts: Record<string, number>;
}) {
  return (
    <div className="mb-5 overflow-x-auto [&::-webkit-scrollbar]:hidden">
      <div className="inline-flex min-w-max items-center gap-1 rounded-xl border border-border/60 bg-card/60 p-1 backdrop-blur">
        {editions.map((e) => {
          const isActive = e === active;
          return (
            <button
              key={e}
              type="button"
              onClick={() => onChange(e)}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-lg"
                  : "text-muted-foreground hover:bg-surface hover:text-foreground"
              }`}
            >
              <span>{e}</span>
              <span
                className={`inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1.5 text-[10px] font-bold ${
                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-surface text-muted-foreground"
                }`}
              >
                {counts[e] ?? 0}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function HeroGallery({
  title,
  movies,
  screenshots,
  fallback,
}: {
  title: string;
  movies: import("@/lib/api").Movie[];
  screenshots: { id: string; url: string }[];
  fallback: string | null;
}) {
  type Slide =
    | { kind: "video"; id: string; src: string; poster: string | null }
    | { kind: "image"; id: string; src: string };

  const slides: Slide[] = [];
  for (const m of movies) {
    const src = m.mp4_480 || m.webm480 || m.mp4Max || m.webmMax || "";
    if (src) {
      slides.push({ kind: "video", id: m.id, src, poster: m.thumbnail ?? null });
    } else if (m.thumbnail) {
      // Steam no longer exposes a direct video URL for this trailer —
      // show the trailer thumbnail instead of an empty player.
      slides.push({ kind: "image", id: m.id, src: m.thumbnail });
    }
  }
  for (const s of screenshots) slides.push({ kind: "image", id: s.id, src: s.url });
  if (slides.length === 0 && fallback) slides.push({ kind: "image", id: "fallback", src: fallback });

  const [index, setIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  const thumbsRef = useRef<HTMLDivElement | null>(null);

  const total = slides.length;
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next]);

  useEffect(() => {
    const strip = thumbsRef.current;
    if (!strip) return;
    const active = strip.querySelector<HTMLButtonElement>(`[data-idx="${index}"]`);
    active?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [index]);

  const goFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else el.requestFullscreen?.().catch(() => {});
  };

  if (total === 0) {
    return (
      <div className="aspect-video w-full rounded-2xl border border-border/60 bg-card" />
    );
  }
  const current = slides[index];

  return (
    <div className="space-y-3">
      <div
        ref={containerRef}
        className="group relative overflow-hidden rounded-2xl border border-border/60 bg-black shadow-2xl"
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          const start = touchStartX.current;
          if (start == null) return;
          const dx = (e.changedTouches[0]?.clientX ?? start) - start;
          if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
          touchStartX.current = null;
        }}
      >
        <div className="aspect-video w-full">
          {current.kind === "video" ? (
            <video
              key={current.id}
              src={current.src}
              poster={current.poster ?? undefined}
              controls
              autoPlay
              muted
              playsInline
              className="h-full w-full object-contain"
            />
          ) : (
            <img
              key={current.id}
              src={current.src}
              alt={title}
              loading="eager"
              className="h-full w-full object-cover"
            />
          )}
        </div>

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous"
              className="absolute left-3 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full border border-white/20 bg-black/50 text-white opacity-0 backdrop-blur transition group-hover:opacity-100 hover:bg-black/70"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next"
              className="absolute right-3 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full border border-white/20 bg-black/50 text-white opacity-0 backdrop-blur transition group-hover:opacity-100 hover:bg-black/70"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        <button
          type="button"
          onClick={goFullscreen}
          aria-label="Fullscreen"
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-lg border border-white/20 bg-black/50 text-white opacity-0 backdrop-blur transition group-hover:opacity-100 hover:bg-black/70"
        >
          <Maximize2 className="h-4 w-4" />
        </button>

        <div className="pointer-events-none absolute bottom-3 left-3 rounded-md bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white/90 backdrop-blur">
          {index + 1} / {total}
        </div>
      </div>

      {total > 1 && (
        <div
          ref={thumbsRef}
          className="flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-border"
        >
          {slides.map((s, i) => {
            const isActive = i === index;
            const thumb = s.kind === "video" ? s.poster ?? "" : s.src;
            return (
              <button
                key={s.id}
                type="button"
                data-idx={i}
                onClick={() => setIndex(i)}
                aria-label={`Slide ${i + 1}`}
                className={`relative aspect-video w-28 shrink-0 overflow-hidden rounded-lg border transition sm:w-32 ${
                  isActive
                    ? "border-primary ring-2 ring-primary/50"
                    : "border-border/60 opacity-70 hover:opacity-100"
                }`}
              >
                {thumb ? (
                  <img src={thumb} alt="" loading="lazy" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-muted" />
                )}
                {s.kind === "video" && (
                  <span className="absolute inset-0 grid place-items-center bg-black/30 text-white">
                    <Play className="h-4 w-4" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ---------------- Game Tabs ---------------- */

type TabId = "overview" | "system" | "languages" | "features" | "additional";

function GameTabs({ game: g, platformList }: { game: Game; platformList: string }) {
  const { t } = useSettings();
  const [active, setActive] = useState<TabId>("overview");

  const hasSystem = !!(
    (g.pcRequirements && (g.pcRequirements.minimum || g.pcRequirements.recommended)) ||
    (g.macRequirements && (g.macRequirements.minimum || g.macRequirements.recommended)) ||
    (g.linuxRequirements && (g.linuxRequirements.minimum || g.linuxRequirements.recommended))
  );
  const hasLanguages = !!((g.languages && g.languages.length > 0) || g.supportedLanguages);
  const hasFeatures = !!(g.categories && g.categories.length > 0);
  const hasAdditional =
    g.metacriticScore != null ||
    !!g.controllerSupport ||
    !!(g.achievements?.total ?? g.achievementsTotal) ||
    !!(g.dlc && g.dlc.length) ||
    !!(g.packages?.packages && g.packages.packages.length) ||
    !!g.website ||
    !!g.legalNotice;

  const tabs = ([
    { id: "overview" as TabId, label: t("game.tabs.overview"), show: true },
    { id: "system" as TabId, label: t("game.tabs.system"), show: hasSystem },
    { id: "languages" as TabId, label: t("game.tabs.languages"), show: hasLanguages },
    { id: "features" as TabId, label: t("game.tabs.features"), show: hasFeatures },
    { id: "additional" as TabId, label: t("game.tabs.additional"), show: hasAdditional },
  ]).filter((tab) => tab.show);


  return (
    <div className="rounded-2xl border border-border/60 bg-card/40">
      {/* Sticky tab bar */}
      <div className="sticky top-0 z-10 -mx-px overflow-x-auto rounded-t-2xl border-b border-border/60 bg-card/95 backdrop-blur [&::-webkit-scrollbar]:hidden">
        <div className="flex min-w-max items-center gap-1 px-2 py-2">
          {tabs.map((tab) => {
            const isActive = active === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActive(tab.id)}
                className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  isActive
                    ? "bg-primary text-primary-foreground shadow"
                    : "text-muted-foreground hover:bg-surface/60 hover:text-foreground"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-5 md:p-7">
        {active === "overview" && (
          <div className="space-y-6">
            {g.shortDescription && (
              <p className="text-sm leading-relaxed text-muted-foreground md:text-base">
                {g.shortDescription}
              </p>
            )}
            <QuickFacts game={g} platformList={platformList} />
          </div>
        )}
        {active === "system" && <SystemRequirementsCard game={g} />}
        {active === "languages" && <SupportedLanguagesCard game={g} />}
        {active === "features" && <SteamFeaturesCard game={g} />}
        {active === "additional" && <AdditionalInfo game={g} />}
      </div>
    </div>
  );
}


/* ---------------- Quick Facts ---------------- */

function QuickFacts({ game: g, platformList }: { game: Game; platformList: string }) {
  const { t } = useSettings();
  const items: { icon: string; label: string; value: React.ReactNode }[] = [];

  if (g.metacriticScore != null) {
    items.push({ icon: "⭐", label: t("game.quick.metacritic"), value: `${g.metacriticScore} / 100` });
  }
  if (g.releaseDate) {
    items.push({
      icon: "📅",
      label: t("game.quick.release"),
      value: new Date(g.releaseDate).toLocaleDateString(),
    });
  }
  items.push({ icon: "🎮", label: t("game.quick.platform"), value: platformList });
  if (g.languages && g.languages.length > 0) {
    items.push({ icon: "🌍", label: t("game.quick.languages"), value: `${g.languages.length}` });
  } else if (g.supportedLanguages) {
    const count = g.supportedLanguages.split(/<br\s*\/?>/i)[0].split(",").filter((s) => s.trim()).length;
    if (count > 0) items.push({ icon: "🌍", label: t("game.quick.languages"), value: `${count}` });
  }
  const achievements = g.achievements?.total ?? g.achievementsTotal;
  if (achievements) {
    items.push({ icon: "🏆", label: t("game.quick.achievements"), value: `${achievements}` });
  }
  if (g.controllerSupport) {
    items.push({
      icon: "🎯",
      label: t("game.quick.controller"),
      value: <span className="capitalize">{g.controllerSupport.replace(/_/g, " ")}</span>,
    });
  }
  if (g.dlc && g.dlc.length > 0) {
    items.push({ icon: "💿", label: t("game.quick.dlc"), value: `${g.dlc.length}` });
  }
  if (g.developer) items.push({ icon: "👨‍💻", label: t("game.quick.developer"), value: g.developer });
  if (g.publisher) items.push({ icon: "🏢", label: t("game.quick.publisher"), value: g.publisher });

  if (items.length === 0) return null;

  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {items.map((it) => (
        <div
          key={it.label}
          className="flex min-w-0 items-start gap-3 rounded-xl border border-border/60 bg-card/60 p-3.5 transition hover:border-primary/40"
        >
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-base leading-none">
            {it.icon}
          </span>
          <div className="min-w-0">
            <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{it.label}</dt>
            <dd className="mt-0.5 min-w-0 truncate text-sm font-semibold text-foreground">{it.value}</dd>
          </div>
        </div>
      ))}
    </dl>
  );
}

/* ---------------- Steam description (collapsed) ---------------- */




function AdditionalInfo({ game: g }: { game: Game }) {
  const { t } = useSettings();

  const items: { icon: React.ReactNode; label: string; value: React.ReactNode }[] = [];

  if (g.metacriticScore != null) {
    items.push({
      icon: <Trophy className="h-4 w-4 text-primary" />,
      label: t("game.additional.metacritic"),
      value: g.metacriticUrl ? (
        <a href={g.metacriticUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">
          {g.metacriticScore} / 100 <ExternalLink className="inline h-3 w-3" />
        </a>
      ) : (
        <span className="font-semibold">{g.metacriticScore} / 100</span>
      ),
    });
  }

  if (g.controllerSupport) {
    items.push({
      icon: <Gamepad2 className="h-4 w-4 text-primary" />,
      label: t("game.additional.controller"),
      value: <span className="capitalize">{g.controllerSupport.replace(/_/g, " ")}</span>,
    });
  }

  const achievementCount = g.achievements?.total ?? g.achievementsTotal;
  if (achievementCount) {
    items.push({
      icon: <Award className="h-4 w-4 text-primary" />,
      label: t("game.additional.achievements"),
      value: <span className="font-semibold">{achievementCount}</span>,
    });
  }

  if (g.dlc && g.dlc.length > 0) {
    items.push({
      icon: <Puzzle className="h-4 w-4 text-primary" />,
      label: t("game.additional.dlc"),
      value: <span className="font-semibold">{g.dlc.length}</span>,
    });
  }

  if (g.packages?.packages && g.packages.packages.length > 0) {
    items.push({
      icon: <Package className="h-4 w-4 text-primary" />,
      label: t("game.additional.packages"),
      value: <span className="font-semibold">{g.packages.packages.length}</span>,
    });
  }

  if (g.website) {
    items.push({
      icon: <Globe className="h-4 w-4 text-primary" />,
      label: t("game.additional.website"),
      value: (
        <a
          href={g.website}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 truncate text-primary hover:underline"
        >
          {g.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
      ),
    });
  }

  if (items.length === 0 && !g.legalNotice) return null;

  return (
    <div className="rounded-2xl border border-border/60 bg-card/60 p-6 md:p-8">
      <h2 className="mb-5 flex items-center gap-2 font-display text-xl font-bold md:text-2xl">
        <FileText className="h-5 w-5 text-primary" /> {t("game.section.additional")}
      </h2>

      {items.length > 0 && (
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <div
              key={it.label}
              className="flex min-w-0 items-start gap-3 rounded-xl border border-border/60 bg-surface/40 p-4"
            >
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/15">
                {it.icon}
              </span>
              <div className="min-w-0">
                <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{it.label}</dt>
                <dd className="mt-0.5 min-w-0 truncate text-sm">{it.value}</dd>
              </div>
            </div>
          ))}
        </dl>
      )}

      {g.legalNotice && (
        <div className="mt-6 rounded-xl border border-border/60 bg-surface/30 p-4">
          <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
            <FileText className="h-3.5 w-3.5" /> {t("game.additional.legal")}
          </div>
          <div
            className="text-xs leading-relaxed text-muted-foreground [&_a]:text-primary"
            dangerouslySetInnerHTML={{ __html: g.legalNotice }}
          />
        </div>
      )}
    </div>
  );
}



function TopOffersPanel({
  game: g,
  rows,
  offerCount,
  formatPrice,
}: {
  game: Game;
  rows: StoreRow[];
  offerCount: number;
  formatPrice: (cents: number | null | undefined, currency?: string) => string;
}) {
  const { t } = useSettings();
  const steamPrice = g.steamPriceFinal ?? null;
  const steamCurrency = g.steamCurrency || "EUR";
  const best = rows[0] ?? null;
  const lowestEffective = best?.effectivePrice ?? best?.price ?? null;
  const savings =
    steamPrice != null && lowestEffective != null && steamPrice > lowestEffective
      ? steamPrice - lowestEffective
      : 0;

  const scrollToOffers = () => {
    document.getElementById("price-comparison")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className="flex h-full min-h-[30rem] flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/80 shadow-2xl backdrop-blur lg:min-h-0">
      <div className="relative flex items-center justify-between gap-4 border-b border-border/60 px-5 py-4">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
        <div className="min-w-0">
          <div className="font-display text-lg font-bold text-foreground">{t("price.topOffers")}</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">{t("price.activeEdition")}</div>
        </div>
        {steamPrice != null && (
          <div className="shrink-0 text-right text-[11px] text-muted-foreground">
            <div>{t("price.steamPrice")} <span className="font-semibold text-foreground">{formatPrice(steamPrice, steamCurrency)}</span></div>
            {savings > 0 && best && (
              <div className="mt-0.5 text-success">{t("price.youSave")} {formatPrice(savings, best.currency)}</div>
            )}
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col divide-y divide-border/60">
        {rows.length > 0 ? (
          rows.map((row, index) => (
            <TopOfferRow key={row.key} row={row} rank={index + 1} formatPrice={formatPrice} />
          ))
        ) : (
          <div className="grid flex-1 place-items-center px-5 py-10 text-center text-sm text-muted-foreground">
            {t("empty.offers")}
          </div>
        )}
      </div>

      <div className="mt-auto border-t border-border/60 bg-surface/40 p-3">
        <Button type="button" onClick={scrollToOffers} className="h-11 w-full uppercase">
          {t("price.viewAllOffers").replace("{count}", String(offerCount))}
          <ArrowDown className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function TopOfferRow({
  row,
  rank,
  formatPrice,
}: {
  row: StoreRow;
  rank: number;
  formatPrice: (cents: number | null | undefined, currency?: string) => string;
}) {
  const { t } = useSettings();
  const platform = normalizePlatform(row);
  const drm = normalizeDrm(row);
  const region = normalizeRegion(row.region);

  return (
    <article className={`relative flex flex-1 flex-col justify-center px-5 py-4 ${rank === 1 ? "bg-primary/5 shadow-[inset_3px_0_0_theme(colors.primary)]" : ""}`}>
      {rank === 1 && (
        <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-primary">
          {t("price.lowest")}
        </div>
      )}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <StoreLogo row={row} size="top" className="-mt-1" />

          <div className="min-w-0">
            <div className="truncate text-base font-bold leading-tight text-foreground">{row.shopName}</div>
            <div className="flex"><ShopTypeLabel slug={row.shopSlug} /></div>
            <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-muted-foreground">
              <PlatformBadge kind={platform} compact />
              <DrmPlatformBadge info={drm} compact />
              <span aria-hidden className="text-border">·</span>
              <RegionBadge info={region} compact />
            </div>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <PriceCell row={row} formatPrice={formatPrice} align="right" large />
          {row.discountPercent != null && row.discountPercent > 0 && (
            <div className="mt-1 text-[10px] font-semibold text-success">−{row.discountPercent}%</div>
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        <StockStatus status={row.stockStatus} />
        <BuyButton row={row} />
      </div>
    </article>
  );
}


/* ---------------- Normalization helpers ---------------- */

function PlatformIcon({ kind, className = "h-4 w-4" }: { kind: PlatformKind; className?: string }) {
  if (kind === "PC") return <Monitor className={className} aria-hidden />;
  if (kind === "Xbox")
    return (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
        <path d="M12 2a10 10 0 0 1 6.7 2.58C15.9 5.66 13.6 8 12 10.1 10.4 8 8.1 5.66 5.3 4.58A10 10 0 0 1 12 2Zm-8.4 4.3C5.6 9 8.9 12.7 10.5 15c-1.7 2.4-4.6 5.4-6.7 6.3A10 10 0 0 1 3.6 6.3Zm16.8 0a10 10 0 0 1-.2 15c-2.1-.9-5-3.9-6.7-6.3 1.6-2.3 4.9-6 7-8.7ZM12 15.9c1.6 2.1 3.9 4.4 6.7 5.5a10 10 0 0 1-13.4 0c2.8-1.1 5.1-3.4 6.7-5.5Z" />
      </svg>
    );
  if (kind === "PlayStation")
    return (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
        <path d="M9.5 3v14.4l3.4 1.1V8.6c0-.8.3-1.4 1-1.4.9 0 1 .9 1 1.7V13c2.1 1 3.7.2 3.7-2.6 0-2.9-1-4.2-4-5.2A17 17 0 0 0 9.5 3ZM3 17.5c1.7 1.4 5.2 1.7 8 .7l.5-.2v-1.8l-3 1c-1.7.6-3.3.4-3.7 0-.5-.5.4-1 1-1.2l5.7-2v-1.7L4 15.1a3 3 0 0 0-2 2c0 .1.4.4 1 .4Zm14.5-4.1c-.9.4-.9 1-.1 1.4l3.5 1.2c1.7.6 3.3.4 3.7 0 .5-.5-.4-1-1-1.2l-5.7-2v.6Z" />
      </svg>
    );
  if (kind === "Nintendo Switch") return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <rect x="3" y="4" width="8" height="16" rx="2.5" />
      <rect x="13" y="4" width="8" height="16" rx="2.5" />
      <circle cx="7" cy="9" r="1" fill="var(--color-background)" />
      <circle cx="17" cy="15" r="1.4" fill="var(--color-background)" />
    </svg>
  );
  return <CircleHelp className={className} aria-hidden />;
}

function PlatformBadge({ kind, compact = false }: { kind: PlatformKind; compact?: boolean }) {
  return (
    <span
      className={`${compact ? "h-6 w-6" : "h-7 w-8 rounded-md border border-border/60 bg-surface/70"} inline-grid shrink-0 place-items-center text-foreground`}
      title={kind === "PC" ? "PC / Windows" : kind}
      aria-label={kind === "PC" ? "PC / Windows" : kind}
    >
      <PlatformIcon kind={kind} className={compact ? "h-4 w-4" : "h-5 w-5"} />
    </span>
  );
}

function DrmMark({ info, className = "h-4 w-4" }: { info: DrmInfo; className?: string }) {
  if (info.mark === "steam") return <SteamMark size={22} />;
  if (info.mark === "gog") {
    return (
      <svg viewBox="0 0 32 18" className={className} aria-hidden>
        <path d="M2 3.5 5.5 1h21L30 3.5v11L26.5 17h-21L2 14.5Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <text x="16" y="12.2" textAnchor="middle" fill="currentColor" fontSize="9.5" fontWeight="900" fontFamily="Arial, sans-serif">GOG</text>
      </svg>
    );
  }
  if (info.mark === "epic") return <span className="text-[8px] font-black leading-none" aria-hidden>EPIC</span>;
  if (info.mark === "ea") return <span className="text-[10px] font-black leading-none" aria-hidden>EA</span>;
  if (info.mark === "ubisoft") return <span className="text-xl font-black leading-none" aria-hidden>◎</span>;
  if (info.mark === "battlenet") return <span className="text-xl leading-none" aria-hidden>✦</span>;
  if (info.mark === "microsoft") return <span className="grid grid-cols-2 gap-px" aria-hidden>{[0,1,2,3].map((n) => <i key={n} className="h-1.5 w-1.5 bg-current" />)}</span>;
  if (info.mark === "rockstar") return <span className="text-xs font-black" aria-hidden>R★</span>;
  if (info.mark === "direct") return <HardDriveDownload className={className} aria-hidden />;
  if (info.mark === "drmfree") return <ShieldCheck className={className} aria-hidden />;
  return <CircleHelp className={className} aria-hidden />;
}

function DrmPlatformBadge({ info, compact = false }: { info: DrmInfo; compact?: boolean }) {
  const { t } = useSettings();
  const label = info.mark === "unknown" ? t("price.unknownDrm") : info.name;
  return (
    <span
      className={`${compact ? "h-6 w-7" : "h-7 w-8 rounded-md border border-border/60 bg-surface/70"} inline-grid shrink-0 place-items-center text-foreground`}
      title={label}
      aria-label={label}
    >
      <DrmMark info={info} className={compact ? "h-5 w-6" : "h-5 w-6"} />
    </span>
  );
}

function FlagIcon({ code }: { code: NonNullable<RegionInfo["flag"]> }) {
  const common = { className: "h-3.5 w-5 shrink-0 overflow-hidden rounded-[2px] ring-1 ring-foreground/15", viewBox: "0 0 20 14", "aria-hidden": true } as const;
  if (code === "de") return <svg {...common}><path fill="#171717" d="M0 0h20v4.67H0z"/><path fill="#d4262e" d="M0 4.67h20v4.66H0z"/><path fill="#f2c500" d="M0 9.33h20V14H0z"/></svg>;
  if (code === "pl") return <svg {...common}><path fill="#fff" d="M0 0h20v7H0z"/><path fill="#dc143c" d="M0 7h20v7H0z"/></svg>;
  if (code === "tr") return <svg {...common}><path fill="#e30a17" d="M0 0h20v14H0z"/><circle cx="8" cy="7" r="4" fill="#fff"/><circle cx="9.2" cy="7" r="3.2" fill="#e30a17"/><path fill="#fff" d="m13.1 5.3.5 1.2 1.3.1-1 .8.3 1.3-1.1-.7-1.1.7.3-1.3-1-.8 1.3-.1z"/></svg>;
  if (code === "br") return <svg {...common}><path fill="#229e45" d="M0 0h20v14H0z"/><path fill="#f7df3e" d="M10 1.5 18 7l-8 5.5L2 7z"/><circle cx="10" cy="7" r="3" fill="#2756a3"/></svg>;
  if (code === "fr" || code === "it") return <svg {...common}><path fill={code === "fr" ? "#1d3f91" : "#169b62"} d="M0 0h6.67v14H0z"/><path fill="#fff" d="M6.67 0h6.66v14H6.67z"/><path fill={code === "fr" ? "#e33b43" : "#ce2b37"} d="M13.33 0H20v14h-6.67z"/></svg>;
  if (code === "es") return <svg {...common}><path fill="#aa151b" d="M0 0h20v14H0z"/><path fill="#f1bf00" d="M0 3.5h20v7H0z"/></svg>;
  if (code === "jp") return <svg {...common}><path fill="#fff" d="M0 0h20v14H0z"/><circle cx="10" cy="7" r="3.5" fill="#bc002d"/></svg>;
  if (code === "cn") return <svg {...common}><path fill="#de2910" d="M0 0h20v14H0z"/><path fill="#ffde00" d="m4 2 .6 1.8h1.9L5 4.9l.6 1.8L4 5.6 2.4 6.7 3 4.9 1.5 3.8h1.9z"/></svg>;
  if (code === "ca") return <svg {...common}><path fill="#d52b1e" d="M0 0h5v14H0zm15 0h5v14h-5z"/><path fill="#fff" d="M5 0h10v14H5z"/><path fill="#d52b1e" d="m10 3 .8 2 1.7-.7-.6 1.7 1.6.8-2.3 1.6.4 2.1h-3.2l.4-2.1-2.3-1.6L8.1 6l-.6-1.7 1.7.7z"/></svg>;
  if (code === "eu") return <svg {...common}><path fill="#164194" d="M0 0h20v14H0z"/>{Array.from({ length: 12 }, (_, i) => { const a = (i * Math.PI) / 6; return <circle key={i} cx={10 + Math.sin(a) * 3.1} cy={7 - Math.cos(a) * 3.1} r=".42" fill="#ffcc00"/>; })}</svg>;
  if (code === "us") return <svg {...common}><path fill="#fff" d="M0 0h20v14H0z"/>{[0,2,4,6,8,10,12].map((y) => <path key={y} fill="#b22234" d={`M0 ${y}h20v1H0z`}/>) }<path fill="#3c3b6e" d="M0 0h8.5v7.5H0z"/><g fill="#fff">{[1.2,3.2,5.2,7.2].flatMap((x) => [1.2,3.2,5.2].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r=".3"/>))}</g></svg>;
  if (code === "gb") return <svg {...common}><path fill="#24437c" d="M0 0h20v14H0z"/><path stroke="#fff" strokeWidth="3" d="m0 0 20 14M20 0 0 14"/><path stroke="#cf142b" strokeWidth="1.3" d="m0 0 20 14M20 0 0 14"/><path fill="#fff" d="M8 0h4v14H8zM0 5h20v4H0z"/><path fill="#cf142b" d="M9 0h2v14H9zM0 6h20v2H0z"/></svg>;
  const fills: Record<string, [string, string, string]> = { kr: ["#fff", "#fff", "#fff"], ru: ["#fff", "#1c57a5", "#d52b1e"], ar: ["#74acdf", "#fff", "#74acdf"], au: ["#173f7a", "#173f7a", "#173f7a"] };
  const colors = fills[code] ?? ["#6b7280", "#f3f4f6", "#6b7280"];
  return <svg {...common}><path fill={colors[0]} d="M0 0h20v4.67H0z"/><path fill={colors[1]} d="M0 4.67h20v4.66H0z"/><path fill={colors[2]} d="M0 9.33h20V14H0z"/></svg>;
}

function RegionBadge({ info, compact = false }: { info: RegionInfo; compact?: boolean }) {
  return (
    <span
      className={`inline-flex min-w-0 items-center gap-1.5 ${compact ? "text-muted-foreground" : info.restricted ? "rounded-md bg-warning/15 px-2 py-1 text-warning ring-1 ring-warning/30" : "rounded-md bg-surface/60 px-2 py-1 text-foreground"}`}
      title={info.label}
    >
      {info.flag ? (
        <FlagIcon code={info.flag} />
      ) : info.unknown ? (
        <CircleHelp className={`${compact ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0`} aria-hidden />
      ) : (
        <Globe className={`${compact ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0`} aria-hidden />
      )}
      <span className="truncate">{info.label}</span>
    </span>
  );
}

/* ---------------- Filter bar ---------------- */

type SortKey = "price" | "discount" | "rating";

interface Filters {
  region: string[];
  drm: string[];
  platform: string[];
  shop: string[];
  sort: SortKey;
}
const emptyFilters: Filters = { region: [], drm: [], platform: [], shop: [], sort: "price" };

function FilterBar({
  rows,
  filters,
  setFilters,
}: {
  rows: StoreRow[];
  filters: Filters;
  setFilters: (f: Filters) => void;
}) {
  const platforms = useMemo(
    () => Array.from(new Set(rows.map(normalizePlatform))),
    [rows],
  );
  const drms = useMemo(() => {
    const set = new Map<string, string>();
    for (const r of rows) {
      const d = normalizeDrm(r);
      if (d) set.set(d.key, d.name);
    }
    return Array.from(set.entries());
  }, [rows]);
  const regions = useMemo(() => {
    const set = new Map<string, string>();
    for (const r of rows) {
      const info = normalizeRegion(r.region);
      set.set(info.key, info.label);
    }
    return Array.from(set.entries());
  }, [rows]);
  const shops = useMemo(
    () => Array.from(new Map(rows.map((r) => [r.shopSlug, r.shopName])).entries()),
    [rows],
  );

  const active =
    filters.region.length > 0 ||
    filters.drm.length > 0 ||
    filters.platform.length > 0 ||
    filters.shop.length > 0 ||
    filters.sort !== "price";

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-border/60 bg-card/60 p-2.5 backdrop-blur">
      <div className="inline-flex items-center gap-1.5 pl-1 pr-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Filter className="h-3.5 w-3.5" /> Filters
      </div>
      <MultiSelect
        label="Platform"
        values={filters.platform}
        onChange={(v) => setFilters({ ...filters, platform: v })}
        options={platforms.map((p) => [p, p] as [string, string])}
      />
      <MultiSelect
        label="Region"
        values={filters.region}
        onChange={(v) => setFilters({ ...filters, region: v })}
        options={regions}
      />
      <MultiSelect
        label="DRM"
        values={filters.drm}
        onChange={(v) => setFilters({ ...filters, drm: v })}
        options={drms}
      />
      <MultiSelect
        label="Shops"
        values={filters.shop}
        onChange={(v) => setFilters({ ...filters, shop: v })}
        options={shops}
      />
      <div className="ml-auto flex items-center gap-2">
        <Select
          value={filters.sort}
          onChange={(v) => setFilters({ ...filters, sort: v as SortKey })}
          options={[
            ["price", "↓ Lowest price"],
            ["discount", "% Highest discount"],
            ["rating", "★ Best rated"],
          ]}
        />
        {active && (
          <button
            type="button"
            onClick={() => setFilters(emptyFilters)}
            className="inline-flex items-center gap-1 rounded-lg border border-border/60 bg-surface/60 px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" /> Reset
          </button>
        )}
      </div>
    </div>
  );
}

function MultiSelect({
  label,
  values,
  onChange,
  options,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  options: [string, string][];
}) {
  const toggle = (v: string) =>
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  const selected = new Set(values);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold outline-none transition hover:border-primary/40 focus:border-primary ${
            values.length > 0
              ? "border-primary/50 bg-primary/10 text-foreground"
              : "border-border/60 bg-surface/70 text-foreground"
          }`}
        >
          {label}
          {values.length > 0 && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
              {values.length}
            </span>
          )}
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-1.5">
        <div className="max-h-64 overflow-y-auto">
          {options.map(([v, l]) => (
            <label
              key={v}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-foreground transition hover:bg-surface/80"
            >
              <Checkbox
                checked={selected.has(v)}
                onCheckedChange={() => toggle(v)}
                aria-label={l}
              />
              <span className="truncate">{l}</span>
            </label>
          ))}
          {options.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">No options</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-border/60 bg-surface/70 px-2.5 py-1.5 text-xs font-semibold text-foreground outline-none transition hover:border-primary/40 focus:border-primary"
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>{l}</option>
      ))}
    </select>
  );
}

/* ---------------- Price table ---------------- */

function PriceTable({
  rows,
  formatPrice,
}: {
  rows: StoreRow[];
  formatPrice: (cents: number | null | undefined, currency?: string) => string;
}) {
  const { t: tHead, offerPrefs } = useSettings();
  const [filters, setFilters] = useState<Filters>(emptyFilters);

  // Apply the homepage region/shop preferences once as the initial filter
  // values. The multi-select filters stay fully independent afterwards.
  const prefsApplied = useRef(false);
  useEffect(() => {
    if (prefsApplied.current || !offerPrefs) return;
    prefsApplied.current = true;
    if (offerPrefs.regions.length === 0 && offerPrefs.shops.length === 0) return;
    setFilters((f) => ({ ...f, region: [...offerPrefs.regions], shop: [...offerPrefs.shops] }));
  }, [offerPrefs]);

  const filtered = useMemo(() => {
    let list = rows.filter((r) => {
      if (filters.platform.length > 0 && !filters.platform.includes(normalizePlatform(r))) return false;
      if (filters.drm.length > 0 && !filters.drm.includes(normalizeDrm(r).key)) return false;
      if (filters.region.length > 0 && !filters.region.includes(normalizeRegion(r.region).key)) return false;
      if (filters.shop.length > 0 && !filters.shop.includes(r.shopSlug)) return false;
      return true;
    });
    const withEff = (r: StoreRow) => comparableEur(r.effectivePrice ?? r.price, r.currency);
    if (filters.sort === "discount") {
      list = [...list].sort((a, b) => (b.discountPercent ?? 0) - (a.discountPercent ?? 0));
    } else if (filters.sort === "rating") {
      // No shop rating yet — Steam first, then price as tiebreaker.
      list = [...list].sort((a, b) => {
        if (a.isSteam !== b.isSteam) return a.isSteam ? -1 : 1;
        return withEff(a) - withEff(b);
      });
    } else {
      list = [...list].sort((a, b) => withEff(a) - withEff(b));
    }
    // Recompute best marker within the filtered/sorted list.
    return list.map((r, i) => ({ ...r, isBest: i === 0 }));
  }, [rows, filters]);

  const steamRef = filtered.find((r) => r.isSteam) ?? rows.find((r) => r.isSteam) ?? null;

  return (
    <div className="space-y-3">
      <FilterBar rows={rows} filters={filters} setFilters={setFilters} />

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-border/60 bg-card/60 p-8 text-center text-sm text-muted-foreground">
          No offers match these filters.
        </div>
      ) : (
        <>
          {/* Desktop */}
          <div className="hidden overflow-hidden rounded-2xl border border-border/60 bg-card/60 backdrop-blur md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface/70 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="w-12 px-4 py-3 text-center">#</th>
                    <th className="px-4 py-3">{tHead("price.store")}</th>
                    <th className="px-4 py-3">{tHead("price.edition")}</th>
                    <th className="px-4 py-3">{tHead("price.platform")}</th>
                    <th className="px-4 py-3 text-center">{tHead("price.drm")}</th>
                    <th className="px-4 py-3">{tHead("price.region")}</th>
                    <th className="px-4 py-3">{tHead("price.delivery")}</th>
                    <th className="px-4 py-3 text-right">{tHead("price.price")}</th>
                    <th className="px-4 py-3 text-right">{tHead("price.savings")}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <PriceRow
                      key={r.key}
                      row={r}
                      rank={i + 1}
                      steamRef={steamRef}
                      formatPrice={formatPrice}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile */}
          <ul className="space-y-3 md:hidden">
            {filtered.map((r, i) => (
              <PriceCardMobile
                key={r.key}
                row={r}
                rank={i + 1}
                steamRef={steamRef}
                formatPrice={formatPrice}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function PriceRow({
  row: r,
  rank,
  steamRef,
  formatPrice,
}: {
  row: StoreRow;
  rank: number;
  steamRef: StoreRow | null;
  formatPrice: (cents: number | null | undefined, currency?: string) => string;
}) {
  const { t } = useSettings();
  const savings = computeSavings(r, steamRef);
  const platform = normalizePlatform(r);
  const drm = normalizeDrm(r);
  const region = normalizeRegion(r.region);
  return (
    <tr
      className={`group border-t border-border/50 align-middle transition ${
        r.isBest
          ? "bg-primary/5 shadow-[inset_0_0_0_1px_theme(colors.primary/30),0_0_40px_-10px_theme(colors.primary/40)]"
          : "hover:bg-surface/40"
      }`}
    >
      <td className="px-4 py-4 text-center">
        <span
          className={`inline-grid h-6 w-6 place-items-center rounded-md text-[11px] font-bold ${
            r.isBest ? "bg-primary text-primary-foreground" : "bg-surface text-muted-foreground"
          }`}
        >
          {rank}
        </span>
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <StoreLogo row={r} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{r.shopName}</span>
              <TrustDot />
            </div>
            {r.isBest ? (
              <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                ★ {t("price.bestDeal")}
              </span>
            ) : (
              <ShopTypeLabel slug={r.shopSlug} />
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-4 text-muted-foreground">{r.edition || "Standard"}</td>
      <td className="px-4 py-4">
        <PlatformBadge kind={platform} />
      </td>
      <td className="px-4 py-4">
        <DrmPlatformBadge info={drm} />
      </td>
      <td className="px-4 py-4"><RegionBadge info={region} /></td>
      <td className="px-4 py-4"><StockPill status={r.stockStatus} /></td>
      <td className="px-4 py-4 text-right">
        <PriceCell row={r} formatPrice={formatPrice} align="right" />
        {savings.showSteamRef && (
          <div className="mt-1 text-[11px] text-muted-foreground">
            Steam{" "}
            <span className="line-through">
              {formatPrice(steamRef!.price!, steamRef!.currency)}
            </span>
          </div>
        )}
      </td>
      <td className="px-4 py-4 text-right">
        {savings.amount > 0 ? (
          <div className="inline-flex flex-col items-end">
            <span className="rounded-md bg-success/15 px-2 py-0.5 text-xs font-bold text-success">
              −{savings.percent}%
            </span>
            <span className="mt-1 text-[11px] font-medium text-success">
              −{formatPrice(savings.amount, r.currency)}
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-4 text-right">
        <BuyButton row={r} />
      </td>
    </tr>
  );
}

function PriceCardMobile({
  row: r,
  rank,
  steamRef,
  formatPrice,
}: {
  row: StoreRow;
  rank: number;
  steamRef: StoreRow | null;
  formatPrice: (cents: number | null | undefined, currency?: string) => string;
}) {
  const { t } = useSettings();
  const savings = computeSavings(r, steamRef);
  const platform = normalizePlatform(r);
  const drm = normalizeDrm(r);
  const region = normalizeRegion(r.region);
  return (
    <li
      className={`overflow-hidden rounded-2xl border p-4 transition ${
        r.isBest
          ? "border-primary/40 bg-primary/5 shadow-[0_0_40px_-10px_theme(colors.primary/50)]"
          : "border-border/60 bg-card/60"
      }`}
    >
      {r.isBest && (
        <div className="mb-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-primary">
          ★ {t("price.bestDeal")}
        </div>
      )}
      <div className="flex items-start gap-3">
        <span
          className={`inline-grid h-6 w-6 shrink-0 place-items-center rounded-md text-[11px] font-bold ${
            r.isBest ? "bg-primary text-primary-foreground" : "bg-surface text-muted-foreground"
          }`}
        >
          {rank}
        </span>
        <StoreLogo row={r} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold">{r.shopName}</span>
            <TrustDot />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
            <PlatformBadge kind={platform} />
            <DrmPlatformBadge info={drm} />
            <RegionBadge info={region} />
            <span className="text-muted-foreground">{r.edition || "Standard"}</span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <PriceCell row={r} formatPrice={formatPrice} align="right" />
          {savings.showSteamRef && (
            <div className="mt-1 text-[10px] text-muted-foreground">
              Steam{" "}
              <span className="line-through">
                {formatPrice(steamRef!.price!, steamRef!.currency)}
              </span>
            </div>
          )}
          {savings.amount > 0 && (
            <div className="mt-1 text-[11px] font-bold text-success">
              −{savings.percent}% · −{formatPrice(savings.amount, r.currency)}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <StockPill status={r.stockStatus} />
        <BuyButton row={r} />
      </div>
    </li>
  );
}

function computeSavings(row: StoreRow, steamRef: StoreRow | null) {
  const ref = steamRef?.price ?? null;
  const price = row.effectivePrice ?? row.price ?? null;
  if (row.isSteam || ref == null || price == null || ref <= price) {
    return { amount: 0, percent: 0, showSteamRef: false };
  }
  const amount = ref - price;
  const percent = Math.round((amount / ref) * 100);
  return { amount, percent, showSteamRef: true };
}

function ShopTypeLabel({ slug }: { slug: string }) {
  const { t } = useSettings();
  const key = shopCategoryLabelKey(slug);
  if (!key) return null;
  return (
    <span className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
      {t(key)}
    </span>
  );
}

function TrustDot() {
  return (
    <span
      title="Verified partner"
      className="inline-block h-1.5 w-1.5 rounded-full bg-success shadow-[0_0_6px_theme(colors.success/70)]"
      aria-hidden
    />
  );
}

function StoreLogo({ row, size = "lg", className }: { row: StoreRow; size?: "sm" | "top" | "lg"; className?: string }) {
  const compact = size === "sm";
  const top = size === "top";
  if (row.isSteam) {
    return (
      <div className={`${compact ? "h-8 w-8 rounded-md" : top ? "h-11 w-11 rounded-lg" : "h-12 w-12 rounded-xl"} grid shrink-0 place-items-center bg-surface-elevated text-foreground ring-1 ring-border/60 ${className ?? ""}`}>
        <SteamMark size={compact ? 20 : top ? 27 : 30} />
      </div>
    );
  }
  return <ShopLogo name={row.shopName} slug={row.shopSlug} logoUrl={row.shopLogo} size={top ? "md" : compact ? "sm" : "lg"} className={cn(top ? "h-11 w-11 rounded-lg" : undefined, className)} />;
}


function StockStatus({ status }: { status: string }) {
  const { t } = useSettings();
  const inStock = status === "in_stock";
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${inStock ? "text-success" : "text-muted-foreground"}`}>
      {inStock ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
      {inStock ? t("price.instantDelivery") : status.replace(/_/g, " ")}
    </span>
  );
}

function StockPill({ status }: { status: string }) {
  return <StockStatus status={status} />;
}

function BuyButton({ row }: { row: StoreRow }) {
  const { t } = useSettings();
  const ctx = useGameCtx();
  const purchasable = row.stockStatus === "in_stock" || row.stockStatus === "preorder";
  const disabled = !row.url || !purchasable;
  return (
    <a
      href={row.url ?? "#"}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={() => { if (!disabled) fireAffiliate(row, ctx); }}
      className={`group/buy inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-xs font-bold uppercase tracking-wide shadow-sm transition-all duration-200 ${
        disabled
          ? "pointer-events-none bg-muted text-muted-foreground"
          : row.isBest
            ? "bg-primary text-primary-foreground shadow-[0_8px_30px_-8px_theme(colors.primary/70)] hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-[0_12px_40px_-8px_theme(colors.primary/90)]"
            : "bg-primary/90 text-primary-foreground hover:-translate-y-0.5 hover:bg-primary"
      }`}
    >
      {t("game.action.buy")}
      <ExternalLink className="h-3.5 w-3.5 transition group-hover/buy:translate-x-0.5" />
    </a>
  );
}

/**
 * Price cell: shows list price (struck) above coupon-adjusted price when a
 * coupon applies, plus a premium clickable coupon badge below.
 */
function PriceCell({
  row: r,
  formatPrice,
  align = "right",
  large = false,
}: {
  row: StoreRow;
  formatPrice: (cents: number | null | undefined, currency?: string) => string;
  align?: "right" | "left";
  large?: boolean;
}) {
  const hasCoupon = !!(r.couponCode && r.effectivePrice != null && r.price != null && r.effectivePrice < r.price);
  const alignCls = align === "right" ? "items-end text-right" : "items-start text-left";
  if (r.price == null) return <span className="text-muted-foreground">—</span>;
  return (
    <div className={`inline-flex min-w-0 flex-col gap-0.5 ${alignCls}`}>
      {hasCoupon && (
        <span className="text-[11px] font-medium text-muted-foreground line-through">
          {formatPrice(r.price, r.currency)}
        </span>
      )}
      <span
        className={`font-display ${large ? "text-xl sm:text-2xl" : "text-lg"} font-black leading-none ${
          r.isBest ? "text-primary" : "text-foreground"
        }`}
      >
        {formatPrice(hasCoupon ? r.effectivePrice! : r.price, r.currency)}
      </span>
      {hasCoupon && <CouponBadge row={r} />}
    </div>
  );
}

/** Premium clickable coupon badge — copies the code on click. */
function CouponBadge({ row: r }: { row: StoreRow }) {
  const [copied, setCopied] = useState(false);
  if (!r.couponCode) return null;
  const onCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(r.couponCode!);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title="Click to copy coupon code"
      className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-gradient-to-r from-primary/15 to-primary/5 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-primary shadow-sm transition hover:border-primary/60 hover:from-primary/25 hover:to-primary/10"
    >
      {copied ? (
        <>
          <Check className="h-3 w-3" /> Code copied
        </>
      ) : (
        <>
          <Tag className="h-3 w-3" />
          <code className="font-mono">{r.couponCode}</code>
          <span className="text-primary/80">·</span>
          <span>−{r.couponPercent ?? 0}%</span>
        </>
      )}
    </button>
  );
}



function SteamMark({ small, size }: { small?: boolean; size?: number } = {}) {
  const px = size ?? (small ? 14 : 18);
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      className="text-white"
    >
      <path d="M12 2a10 10 0 0 0-9.94 8.94l5.4 2.23a2.75 2.75 0 0 1 1.68-.57l2.5-3.63v-.05a3.75 3.75 0 1 1 3.75 3.75h-.09l-3.57 2.55a2.75 2.75 0 0 1-4.85 2.06l-3.85-1.6A10 10 0 1 0 12 2Zm-3.03 14.86-1.24-.51a2.08 2.08 0 1 0 1.24.51Zm7.53-3.36a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
    </svg>
  );
}

// Suppress unused-import warning when Shop type isn't referenced directly
export type __EnsureShop = Shop;
